import throttle from "lodash/throttle";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "styled-components";

import { useIsMounted } from "@pancakeswap/hooks";
import { useTranslation } from "@pancakeswap/localization";
// import useActiveWeb3React from "hooks/useActiveWeb3React";
import { AtomBox } from "../../components/AtomBox";
import { Box } from "../../components/Box";
import Flex from "../../components/Box/Flex";
import LangSelector from "../../components/LangSelector/LangSelector";
import Link from "../../components/Link/Link";
import { useMatchBreakpoints } from "../../contexts";
import Logo from "./components/Logo";
import { INFO_CHAIN, MENU_HEIGHT, TOP_BANNER_HEIGHT, TOP_BANNER_HEIGHT_MOBILE } from "./config";
import { MenuContext } from "./context";
import { NavProps } from "./types";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const BodyWrapper = styled(Box)`
  position: relative;
  display: flex;
  max-width: 100vw;
`;

const StyledNav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1440px;
  height: ${MENU_HEIGHT}px;
  background-color: #090d17;
  transform: translate3d(0, 0, 0);

  @media screen and (max-width: 1440px) {
    padding: 0px 16px;
  }
`;

const FixedContainer = styled("div").withConfig({
  shouldForwardProp: (props) => !["showMenu"].includes(props),
})<{ showMenu: boolean; height: number }>`
  display: flex;
  justify-content: center;
  position: fixed;
  top: ${({ showMenu, height }) => (showMenu ? 0 : `-${height}px`)};
  left: 0;
  transition: top 0.2s;
  height: ${({ height }) => `${height}px`};
  width: 100%;
  z-index: 20;
`;

const Inner = styled.div`
  flex-grow: 1;
  transition: margin-top 0.2s, margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translate3d(0, 0, 0);
  max-width: 100%;
`;

const Menu: React.FC<React.PropsWithChildren<NavProps>> = ({
  linkComponent = "a",
  banner,
  rightSide,
  currentLang,
  setLang,
  links,
  langs,
  children,
}) => {
  const { query } = useRouter();
  const { t } = useTranslation();
  // const { chainId } = useActiveWeb3React();
  const { isMobile } = useMatchBreakpoints();
  const isMounted = useIsMounted();
  const [showMenu, setShowMenu] = useState(true);
  const [currentTab, setCurrentTab] = useState("/");
  const refPrevOffset = useRef(typeof window === "undefined" ? 0 : window.pageYOffset);

  const topBannerHeight = isMobile ? TOP_BANNER_HEIGHT_MOBILE : TOP_BANNER_HEIGHT;

  const totalTopMenuHeight = isMounted && banner ? MENU_HEIGHT + topBannerHeight : MENU_HEIGHT;

  useEffect(() => {
    setCurrentTab(window.location.pathname);
    const handleScroll = () => {
      const currentOffset = window.pageYOffset;
      const isBottomOfPage = window.document.body.clientHeight === currentOffset + window.innerHeight;
      const isTopOfPage = currentOffset === 0;
      // Always show the menu when user reach the top
      if (isTopOfPage) {
        setShowMenu(true);
      }
      // Avoid triggering anything at the bottom because of layout shift
      else if (!isBottomOfPage) {
        if (currentOffset < refPrevOffset.current || currentOffset <= totalTopMenuHeight) {
          // Has scroll up
          setShowMenu(true);
        } else {
          // Has scroll down
          setShowMenu(false);
        }
      }
      refPrevOffset.current = currentOffset;
    };
    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [totalTopMenuHeight]);

  // Find the home link if provided
  const homeLink = links.find((link) => link?.label === "Home");

  const getLinkSwap = useCallback(() => {
    let tempLink =
      "https://miaswap.io/?inputCurrency=0xff276c6bca1F66Fd54a8915e830735D6ab0C7B09&outputCurrency=0xa6b9579563A48a0540BB53853Ae0947972371169";

    switch (query.chain) {
      case "bnb":
      case "bsc":
        tempLink = `${INFO_CHAIN[query.chain].url}?inputCurrency=${INFO_CHAIN[query.chain].usdt}&outputCurrency=${
          INFO_CHAIN[query.chain].vndc
        }`;
        break;
      case "polygon":
        tempLink = `${INFO_CHAIN[query.chain].url}/${INFO_CHAIN[query.chain].chain}/${
          INFO_CHAIN[query.chain].vndc
        }?chain=${INFO_CHAIN[query.chain].chain}&inputCurrency=${INFO_CHAIN[query.chain].usdt}`;
        break;
      default:
        break;
    }

    return tempLink;
  }, [query.chain]);

  const providerValue = useMemo(() => ({ linkComponent }), [linkComponent]);

  return (
    <MenuContext.Provider value={providerValue}>
      <AtomBox
        asChild
        minHeight={{
          xs: "auto",
          md: "100vh",
        }}
      >
        <Wrapper>
          <FixedContainer showMenu={showMenu} height={totalTopMenuHeight}>
            <StyledNav>
              <Flex>
                <Logo href={homeLink?.href ?? "/"} />
                {!isMobile && (
                  <Link
                    href={getLinkSwap()}
                    color="#66748A"
                    bold
                    style={{
                      marginRight: 50,
                      marginLeft: 60,
                      textDecoration: "none",
                      fontSize: 18,
                      lineHeight: "110%",
                    }}
                    target="_blank"
                  >
                    {t("Swap")}
                  </Link>
                )}
                {!isMobile && (
                  <Link
                    href="/"
                    color={currentTab === "/" ? "#fff" : "#66748A"}
                    bold
                    style={{
                      marginRight: 50,
                      textDecoration: "none",
                      fontSize: 18,
                      lineHeight: "110%",
                    }}
                  >
                    Mint
                  </Link>
                )}
                {!isMobile && (
                  <Link
                    color={currentTab.includes("analytics") ? "#fff" : "#66748A"}
                    href="/analytics"
                    bold
                    style={{
                      marginRight: 50,
                      textDecoration: "none",
                      fontSize: 18,
                      lineHeight: "110%",
                    }}
                  >
                    {t("Analytics")}
                  </Link>
                )}
                {!isMobile && (
                  <Link
                    color="#66748A"
                    href="https://blog.vndc.io/"
                    bold
                    style={{
                      textDecoration: "none",
                      fontSize: 18,
                      lineHeight: "110%",
                    }}
                    target="_blank"
                  >
                    Blog
                  </Link>
                )}
              </Flex>
              <Flex alignItems="center" height="100%">
                <Box mt="4px">
                  <LangSelector
                    currentLang={currentLang}
                    langs={langs}
                    setLang={setLang}
                    buttonScale="xs"
                    color="textSubtle"
                    hideLanguage
                  />
                </Box>
                {rightSide}
              </Flex>
            </StyledNav>
          </FixedContainer>
          <BodyWrapper mt={`${totalTopMenuHeight + 1}px`}>
            <Inner>{children}</Inner>
          </BodyWrapper>
        </Wrapper>
      </AtomBox>
    </MenuContext.Provider>
  );
};

export default Menu;
